import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Building2, ArrowRight, Shield, UserPlus, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import { authAPI } from '../lib/api';

export default function LoginPage() {
  const { user, login, register } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('login'); // 'login', 'request', 'register', 'success'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setSessionExpired(true);
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach server. Please try again later.');
      } else if (err.response.status >= 500) {
        setError('Server is temporarily unavailable. Please try again in a moment.');
      } else {
        setError(err.response?.data?.detail || 'Authentication failed');
      }
    }
    setLoading(false);
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.requestAccess({ email, name, reason });
      setMode('success');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // If user is already approved, redirect to register
      if (detail.includes('already approved') || detail.includes('register directly')) {
        setError('');
        setMode('register');
      } else {
        setError(detail || 'Failed to submit request');
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  const renderLoginForm = () => (
    <>
      <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
        Welcome Back
      </h3>
      <p className="text-slate-500 text-sm mb-8">
        Sign in with your credentials
      </p>

      {sessionExpired && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-sm px-4 py-3 mb-6 text-sm flex items-center gap-2" data-testid="session-expired-notice">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>Your session has expired. Please sign in again.</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-sm px-4 py-3 mb-6 text-sm" data-testid="auth-error">
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-5">
        <div>
          <Label htmlFor="email" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="login-email-input"
            required
          />
        </div>

        <div>
          <Label htmlFor="password" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="login-password-input"
            required
          />
          <div className="text-right mt-1">
            <a href="/forgot-password" className="text-xs text-slate-500 hover:text-red-700 transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </a>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-red-700 hover:bg-red-800 text-white rounded-sm h-11 font-medium shadow-sm active:scale-[0.98] transition-transform"
          data-testid="login-submit-btn"
        >
          {loading ? 'Please wait...' : 'Sign In'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>

      <div className="mt-6 pt-6 border-t border-slate-200">
        <p className="text-sm text-slate-500 mb-3 text-center">Don't have an account?</p>
        <Button
          variant="outline"
          onClick={() => { setMode('request'); setError(''); }}
          className="w-full rounded-sm border-red-200 text-red-700 hover:bg-red-50"
          data-testid="request-access-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Request Access
        </Button>
        <p className="text-center mt-3">
          <button
            onClick={() => { setMode('register'); setError(''); }}
            className="text-xs text-slate-400 hover:text-red-700 transition-colors"
            data-testid="already-approved-btn"
          >
            Already approved? Register here
          </button>
        </p>
      </div>
    </>
  );

  const renderRequestForm = () => (
    <>
      <button 
        onClick={() => { setMode('login'); setError(''); }}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-700 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to login
      </button>

      <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
        Request Access
      </h3>
      <p className="text-slate-500 text-sm mb-8">
        Submit your request and an admin will review it
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-sm px-4 py-3 mb-6 text-sm" data-testid="auth-error">
          {error}
        </div>
      )}

      <form onSubmit={handleRequestAccess} className="space-y-5">
        <div>
          <Label htmlFor="req-name" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Full Name
          </Label>
          <Input
            id="req-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="request-name-input"
            required
          />
        </div>

        <div>
          <Label htmlFor="req-email" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Email Address
          </Label>
          <Input
            id="req-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="request-email-input"
            required
          />
        </div>

        <div>
          <Label htmlFor="req-reason" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Reason for Access <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Textarea
            id="req-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tell us why you need access to the system..."
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white min-h-[80px]"
            data-testid="request-reason-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-red-700 hover:bg-red-800 text-white rounded-sm h-11 font-medium shadow-sm active:scale-[0.98] transition-transform"
          data-testid="submit-request-btn"
        >
          {loading ? 'Submitting...' : 'Submit Request'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-sm">
        <p className="text-xs text-amber-800">
          <strong>Note:</strong> Access requests are reviewed by administrators. 
          You will receive an email notification once your request is approved.
        </p>
      </div>
    </>
  );

  const renderSuccess = () => (
    <div className="text-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-8 h-8 text-emerald-600" />
      </div>
      
      <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
        Request Submitted!
      </h3>
      <p className="text-slate-500 text-sm mb-8">
        Your access request has been submitted successfully.
      </p>

      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-6 text-left">
        <p className="text-sm text-slate-600">
          <strong>What happens next?</strong>
        </p>
        <ul className="text-sm text-slate-500 mt-2 space-y-1">
          <li>• An administrator will review your request</li>
          <li>• You'll receive an email when approved</li>
          <li>• After approval, you can create your account</li>
        </ul>
      </div>

      <Button
        onClick={() => { setMode('login'); setEmail(''); setName(''); setReason(''); }}
        className="w-full bg-red-700 hover:bg-red-800 text-white rounded-sm h-11"
        data-testid="back-to-login-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
      </Button>
    </div>
  );

  const renderRegisterForm = () => (
    <>
      <button 
        onClick={() => { setMode('login'); setError(''); }}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-700 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to login
      </button>

      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mb-6">
        <p className="text-sm text-emerald-800">
          <strong>Your email is approved!</strong> Create your account below.
        </p>
      </div>

      <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
        Create Account
      </h3>
      <p className="text-slate-500 text-sm mb-8">
        Set up your password to complete registration
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-sm px-4 py-3 mb-6 text-sm" data-testid="auth-error">
          {error}
        </div>
      )}

      <form onSubmit={handleRegister} className="space-y-5">
        <div>
          <Label htmlFor="reg-name" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Full Name
          </Label>
          <Input
            id="reg-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="register-name-input"
            required
          />
        </div>

        <div>
          <Label htmlFor="reg-email" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Email Address
          </Label>
          <Input
            id="reg-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="register-email-input"
            required
          />
        </div>

        <div>
          <Label htmlFor="reg-password" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
            Password
          </Label>
          <Input
            id="reg-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a strong password"
            className="rounded-sm border-slate-300 focus:border-red-700 focus:ring-red-700/20 bg-slate-50 focus:bg-white h-11"
            data-testid="register-password-input"
            required
          />
          <p className="text-xs text-slate-400 mt-1">Min 12 chars, uppercase, lowercase, and number required</p>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-red-700 hover:bg-red-800 text-white rounded-sm h-11 font-medium shadow-sm active:scale-[0.98] transition-transform"
          data-testid="register-submit-btn"
        >
          {loading ? 'Creating account...' : 'Create Account'}
          {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </form>
    </>
  );

  return (
    <div className="login-container" data-testid="login-page">
      {/* Left - Image */}
      <div
        className="login-left"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1765062165737-ed4b47014fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDgxODIzMXww&ixlib=rb-4.1.0&q=85)` }}
      >
        <div className="relative z-10 flex flex-col justify-end h-full p-12">
          <h2
            className="text-white text-5xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            COMMERCIAL<br />LEASE<br />MANAGEMENT
          </h2>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Enterprise-grade platform for managing rental properties, invoices, tenants, and analytics in one secure system.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="login-right">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 bg-red-700 rounded-sm flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                SIMLA TRIOLET
              </h1>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Manager</span>
            </div>
          </div>

          {mode === 'login' && renderLoginForm()}
          {mode === 'request' && renderRequestForm()}
          {mode === 'register' && renderRegisterForm()}
          {mode === 'success' && renderSuccess()}

          {(mode !== 'success') && (
            <div className="mt-8 flex items-center gap-2 text-[11px] text-slate-400">
              <Shield className="w-3.5 h-3.5" />
              <span>Secured with JWT authentication &middot; Admin approval required</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
